import React, { useState } from "react";
import { Box, Typography, Dialog, IconButton } from "@mui/material";
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import { Block } from "@mui/icons-material";

/**
 * Horizontal slider of document thumbnails with a click-to-zoom lightbox.
 * Filters out empty URLs and caps the strip at 5 thumbnails.
 */
const ImagePreviewBox: React.FC<{ imageUrls?: string[], altText?: string }> = ({ imageUrls = [], altText }) => {
  const [open, setOpen] = useState(false);
  const [activeImage, setActiveImage] = useState<string>("");

  const handleOpen = (e: React.MouseEvent, url: string) => {
    e.stopPropagation();
    setActiveImage(url);
    setOpen(true);
  };
  const handleClose = () => setOpen(false);

  // Filter out empty strings just in case
  const validUrls = imageUrls.filter(u => !!u);

  return (
      <>
        <Box sx={{
          display: 'flex',
          gap: 1,
          flexWrap: 'nowrap', // Force single line
          overflowX: 'auto',  // Enable horizontal scrolling
          width: '100%',
          maxWidth: 200,      // Constrain width in grid
          pb: 1,              // Padding for scrollbar
          '&::-webkit-scrollbar': { height: 6 },
          '&::-webkit-scrollbar-thumb': { backgroundColor: '#ccc', borderRadius: 4 }
        }}>
          {validUrls.length > 0 ? (
              validUrls.slice(0, 5).map((url, index) => (
                  <Box
                      key={index}
                      sx={{
                        position: 'relative',
                        flexShrink: 0, // Prevent shrinking
                        height: 70,
                        width: 90,
                        bgcolor: '#f2f2f2',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: 2,
                        boxShadow: 1,
                        cursor: 'pointer',
                        overflow: 'hidden',
                        '&:hover .lens-icon': { opacity: 1 },
                      }}
                      onClick={(e) => handleOpen(e, url)}
                  >
                    <img
                        src={url}
                        alt={`${altText} ${index + 1}`}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                    />
                    <Box
                        className="lens-icon"
                        sx={{
                          position: 'absolute',
                          top: 2,
                          right: 2,
                          bgcolor: 'rgba(255,255,255,0.8)',
                          borderRadius: '50%',
                          opacity: 0,
                          transition: 'opacity 0.2s',
                          p: 0.2,
                        }}
                    >
                      <ZoomInIcon style={{ fontSize: 16 }} color="action" />
                    </Box>
                  </Box>
              ))
          ) : (
              <Box
                  sx={{
                    height: 70,
                    width: 90,
                    bgcolor: '#f2f2f2',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 2,
                    border: '1px dashed #ccc'
                  }}
              >
                <Typography color="textSecondary" fontSize="0.7rem">
                  No Image
                </Typography>
              </Box>
          )}
        </Box>
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="lg"
            disableEnforceFocus
        >
          <Box sx={{
            p: 2,
            bgcolor: 'background.paper',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative'
          }}>
            <IconButton
                onClick={handleClose}
                sx={{ position: 'absolute', top: 5, right: 5, bgcolor: '#fff' }}
            >
              <Block fontSize="small"/>
            </IconButton>
            {activeImage && (
                <img
                    src={activeImage}
                    alt={altText}
                    style={{ maxHeight: '85vh', maxWidth: '90vw', borderRadius: 8 }}
                />
            )}
          </Box>
        </Dialog>
      </>
  );
};

export default ImagePreviewBox;
